import React, { useMemo, useState } from 'react';
import { makeStyles } from '@mui/styles';
import AccordionComponent from '../AccordionComponent';

// components

// material ui imports
import { Typography, Box } from '@mui/material';
import translationService from '../../services/translator.service';
import { AppAccordion, AppAccordionSummary, AppAccordionDetails } from '../primitives/AppAccordion';

// Icones
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { utilService } from '../../services/util.service';

// enums
import { FormatDateEnum } from '../../enums/FormatDateEnum';

const PLACEHOLDER = '-';
const MAIN_ACCORDION_KEY = 'charging-strategy-main';

const useLocalStyles = makeStyles((theme) => ({
    root: {
        width: '100%',
    },
    title: {
        ...theme.typography.h6,
        fontWeight: theme.typography.fontWeightBold,
        color: theme.palette.text.primary,
    },
    boldTest: {
        ...theme.typography.mobileBodySm,
        fontWeight: theme.typography.fontWeightBold,
        color: theme.palette.text.primary,
    },
    partAccordionRow: {
        width: '100%',
        display: 'flex',
        flexDirection: 'row',
        justifyContent: 'space-evenly',
    },
    partSummaryWrap: {
        width: '100%',
        flexShrink: 0,
    },
    partSummaryChips: {
        width: '100%',
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(1.25),
    },
    timeChip: {
        backgroundColor: theme.palette.info.light,
        color: theme.palette.info.dark,
    },
    fullWidth: {
        width: '100%',
    },
    oneTimeServiceDivider: {
        width: '100%',
        borderTop: `1px solid ${theme.palette.divider}`,
        marginTop: theme.spacing(1),
        marginBottom: theme.spacing(1),
    },
    detailsList: {
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
        gap: theme.spacing(0.5),
    },
    detailsRow: {
        width: '100%',
        display: 'flex',
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'center',
        gap: theme.spacing(1),
    },
    detailsKey: {
        ...theme.typography.mobileLabelSm,
        color: theme.palette.text.primary,
    },
    detailsValue: {
        ...theme.typography.mobileBodySm,
        color: theme.palette.text.primary,
        textAlign: 'end',
    },
}));

const ChargingStrategySummaryComponent = ({ chargingStrategyTotalData, handleShowChargingDetails, translatedData }) => {
    const localClasses = useLocalStyles();
    const [accordionExpandedParts, setAccordionExpandedParts] = useState({});

    const t = (key) => translationService.getTranslation(translatedData, key);

    const formatPartTime = (value) => {
        const formatted = utilService.formatDateTime(value, FormatDateEnum.Time);
        if (!formatted || typeof formatted !== 'string') {
            return PLACEHOLDER;
        }

        const [hours = '00', minutes = '00'] = formatted.split(':');
        return `${hours}:${minutes}`;
    };

    const formatPartTimeRange = (startTime, endTime) => `${formatPartTime(startTime)} - ${formatPartTime(endTime)}`;

    const formatPartPrice = (value) => {
        const parsed = Number(value);
        if (!Number.isFinite(parsed)) {
            return PLACEHOLDER;
        }

        return `${parsed.toLocaleString('en-US', {
            minimumFractionDigits: 0,
            maximumFractionDigits: 2,
            useGrouping: false,
        })}₪`;
    };

    const getPartSummaryTotal = (partTotalData) => {
        const detailValues = [partTotalData?.totalServicePrice, partTotalData?.totalElectricityPrice, partTotalData?.totalParkingPrice];
        const detailNumbers = detailValues.map((value) => Number(value));
        const hasAllDetailValues = detailNumbers.every((value) => Number.isFinite(value));

        if (hasAllDetailValues) {
            return detailNumbers.reduce((sum, value) => sum + value, 0);
        }

        const includeVatTotal = Number(partTotalData?.totalPriceIncludeVat);
        if (Number.isFinite(includeVatTotal)) {
            return includeVatTotal;
        }

        const baseTotal = Number(partTotalData?.totalPrice);
        if (Number.isFinite(baseTotal)) {
            return baseTotal;
        }

        return null;
    };

    // eslint-disable-next-line no-unused-vars
    const formatPartSummaryTotal = (partTotalData) => {
        const summaryTotal = getPartSummaryTotal(partTotalData);
        if (summaryTotal == null) {
            return PLACEHOLDER;
        }

        return formatPartPrice(summaryTotal);
    };

    const getPartRows = (partTotalData) => [
        {
            key: 'consumptionHours',
            label: t('strategyCostBreakdown.consumptionHours'),
            value: formatPartTimeRange(partTotalData.startTime, partTotalData.endTime),
        },
        {
            key: 'serviceCost',
            label: t('strategyCostBreakdown.serviceCost'),
            value: formatPartPrice(partTotalData.totalServicePrice),
        },
        {
            key: 'electricityCost',
            label: t('strategyCostBreakdown.electricityCost'),
            value: formatPartPrice(partTotalData.totalElectricityPrice),
        },
        {
            key: 'parkingCost',
            label: t('strategyCostBreakdown.parkingCost'),
            value: formatPartPrice(partTotalData.totalParkingPrice),
        },
    ];

    const isMainAccordionExpanded = Boolean(accordionExpandedParts[MAIN_ACCORDION_KEY]);

    const handleAccordionExpandedPartsChange = (updater) => {
        setAccordionExpandedParts((prev) => {
            const next = typeof updater === 'function' ? updater(prev || {}) : updater || {};
            const wasMainExpanded = Boolean(prev?.[MAIN_ACCORDION_KEY]);
            const isNowMainExpanded = Boolean(next[MAIN_ACCORDION_KEY]);

            if (isNowMainExpanded && !wasMainExpanded) {
                handleShowChargingDetails();
            }

            return next;
        });
    };

    const sortedData = useMemo(() => {
        const source = Array.isArray(chargingStrategyTotalData) ? chargingStrategyTotalData : [];
        return [...source].sort((a, b) => new Date(a.startTime) - new Date(b.startTime));
    }, [chargingStrategyTotalData]);

    return (
        <Box className={localClasses.root}>
            <AccordionComponent
                keyIndex={MAIN_ACCORDION_KEY}
                title={t('labels.chargingStrategyDetails')}
                titleFontSizePx={14}
                TransitionProps={{ unmountOnExit: true }}
                compactSpacing={true}
                borderVariant='bottom-when-collapsed'
                expanded={isMainAccordionExpanded}
                setAccordionExpandedParts={handleAccordionExpandedPartsChange}
                accordionExpandedParts={accordionExpandedParts}
                options={
                    <>
                        {sortedData.map((partTotalData, partIndex) => {
                            const partAccordionKey = `${partTotalData.startTime}-${partTotalData.partId || partIndex}`;

                            return (
                                <Box key={partAccordionKey} className={localClasses.partAccordionRow}>
                                    <AppAccordion
                                        expanded={Boolean(accordionExpandedParts[partAccordionKey])}
                                        className={localClasses.fullWidth}
                                        onChange={(e, expanded) => {
                                            setAccordionExpandedParts((prev) => ({
                                                ...prev,
                                                [partAccordionKey]: expanded,
                                            }));
                                        }}>
                                        <AppAccordionSummary expandIcon={<ExpandMoreIcon />}>
                                            <Box className={localClasses.partSummaryWrap}>
                                                <Box className={localClasses.partSummaryChips}>
                                                    <Typography className={localClasses.detailsKey}>{formatPartTimeRange(partTotalData.startTime, partTotalData.endTime)}</Typography>
                                                    {/* <Chip icon={<MonetizationOnIcon fontSize='small' />} label={` ${formatPartSummaryTotal(partTotalData)}`} /> */}
                                                    {/* <Chip icon={<AccessTimeIcon fontSize='small' />} label={formatPartTimeRange(partTotalData.startTime, partTotalData.endTime)} className={localClasses.timeChip} /> */}
                                                </Box>
                                            </Box>
                                        </AppAccordionSummary>

                                        <AppAccordionDetails className={localClasses.fullWidth}>
                                            <Box className={localClasses.fullWidth}>
                                                <Box className={localClasses.detailsList}>
                                                    {getPartRows(partTotalData).map((row) => (
                                                        <Box key={row.key} className={localClasses.detailsRow}>
                                                            <Typography className={localClasses.detailsKey}>{row.label}</Typography>
                                                            <Typography className={localClasses.detailsValue}>{row.value || PLACEHOLDER}</Typography>
                                                        </Box>
                                                    ))}
                                                </Box>
                                                {partIndex === 0 && (
                                                    <Box className={localClasses.fullWidth}>
                                                        <Box className={localClasses.oneTimeServiceDivider} />
                                                        <Typography className={localClasses.boldTest}>{t('messages.servicesChargeOnlyOnce')}</Typography>
                                                    </Box>
                                                )}
                                            </Box>
                                        </AppAccordionDetails>
                                    </AppAccordion>
                                </Box>
                            );
                        })}
                    </>
                }
            />
        </Box>
    );
};

export default ChargingStrategySummaryComponent;
